import React, { Component } from 'react';
import '../styles/GlobalStyle.css';
import ColType from '../Types';
import { db, firebase } from '../firebase/Firebase';
import { Form, Input, Button, Icon, Modal, notification, DatePicker } from 'antd';

const { TextArea } = Input;
const FormItem = Form.Item;

const successMessage = (description) => {
    notification.open({
        type: 'success',
        message: description,
        placement: 'bottomRight'
    });
};

const errorMessage = (description) => {
    notification.open({
        type: 'error',
        message: description,
        placement: 'bottomRight'
    });
};

// Fields that are no longer collected, but are still written as empty
// strings so the data shape stays backward compatible if they return.
const deprecatedFields = ['title', 'plannedBy', 'goal', 'teacherSupport'];

class ProfessionalLearningReportForm extends Component {
    state = {
        modalVisible: false,
        submitting: false,
        formData: [
            {
                label: 'School Improvement Goals',
                fieldDecorator: 'description',
                placeholder: 'Enter the goals from your school improvement report',
                type: 'textarea',
                required: true
            },
            {
                label: 'Administration Support',
                fieldDecorator: 'adminSupport',
                placeholder: 'How will administration support these goals?',
                type: 'textarea',
                required: true
            },
            {
                label: 'Coach Support',
                fieldDecorator: 'coachSupport',
                placeholder: 'How will coaches support these goals?',
                type: 'textarea',
                required: true
            },
            {
                label: 'First Reflection (Optional)',
                fieldDecorator: 'reflection1',
                placeholder: 'Add initial thoughts, progress notes, or early insights',
                type: 'textarea',
                required: false
            },
            {
                label: 'First Reflection Date',
                fieldDecorator: 'reflection1Date',
                type: 'date',
                required: false
            },
            {
                label: 'Second Reflection (Optional)',
                fieldDecorator: 'reflection2',
                placeholder: 'Add follow-up thoughts, progress updates, or final insights',
                type: 'textarea',
                required: false
            },
            {
                label: 'Second Reflection Date',
                fieldDecorator: 'reflection2Date',
                type: 'date',
                required: false
            },
            {
                label: 'Our Solutions',
                fieldDecorator: 'solutions',
                placeholder: 'Describe the solutions you are putting in place to reach these goals',
                type: 'textarea',
                required: true
            },
        ]
    };

    showModal = () => {
        this.setState({ modalVisible: true });
    };

    handleCancel = () => {
        this.setState({ modalVisible: false });
    };

    handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!(this.props.schoolId && this.props.districtId && this.props.personId)) {
            console.error('Missing required props in form', this.props);
            return;
        }

        this.props.form.validateFields((err, values) => {
            if (!err) {
                this.setState({ submitting: true });

                values.districtId = this.props.districtId;
                values.schoolId = this.props.schoolId;
                values.personId = this.props.personId;
                values.timeStamp = firebase.firestore.FieldValue.serverTimestamp();

                // Remove empty optional fields
                Object.keys(values).forEach(key => {
                    if (values[key] === undefined || values[key] === '') {
                        delete values[key];
                    }
                });

                // Convert moment objects to JavaScript Date objects
                if (values.hasOwnProperty('reflection1Date') && values.reflection1Date) {
                    values.reflection1Date = values.reflection1Date.toDate();
                }
                if (values.hasOwnProperty('reflection2Date') && values.reflection2Date) {
                    values.reflection2Date = values.reflection2Date.toDate();
                }

                // Backward compatibility: keep removed fields on the document as
                // empty strings in case they are brought back later.
                deprecatedFields.forEach(field => {
                    values[field] = '';
                });

                db.collection(ColType.professionalLearningReport)
                    .add(values)
                    .then((docRef) => {
                        console.log("Document written with ID: ", docRef.id);
                        successMessage('School improvement goals added.');
                        this.setState({ submitting: false });
                        this.props.form.resetFields();
                        this.handleCancel();
                    })
                    .catch((error) => {
                        console.error("Error adding document: ", error);
                        errorMessage('Error saving goals. Please try again.');
                        this.setState({ submitting: false });
                    });
            }
        });
    };

    render() {
        const { getFieldDecorator } = this.props.form;

        const formItemLayout = {
            labelCol: { xs: { span: 24 }, sm: { span: 24 } },
            wrapperCol: { xs: { span: 24 }, sm: { span: 24 } },
        };

        return (
            <div className="form-container">
                <Button type="primary" size="large" onClick={this.showModal} className="lg-btn add-goals-btn">
                    <Icon type="plus" /> Add Goals
                </Button>

                <Modal
                    title={
                        <div className="modal-header">
                            <img src="/dotit-logo-color-new.png" alt="logo" className="modal-logo" />
                            <span>Our School Improvement Reports</span>
                        </div>
                    }
                    visible={this.state.modalVisible}
                    width={800}
                    footer={null}
                    onCancel={this.handleCancel}
                    className="learning-modal"
                >
                    <Form onSubmit={this.handleSubmit} className="learning-form">
                        <div className="form-section">
                            <h3>Goals</h3>
                            <p className="section-description">Enter the goals from your school improvement reports</p>
                            {this.state.formData.slice(0, 1).map((item, index) => (
                                <FormItem {...formItemLayout} label={item.label} key={index} className="form-item">
                                    {getFieldDecorator(item.fieldDecorator, {
                                        rules: [{ required: item.required, message: `${item.label} is required` }],
                                    })(
                                        <TextArea
                                            rows={5}
                                            size="large"
                                            placeholder={item.placeholder}
                                            className="textarea-field"
                                        />
                                    )}
                                </FormItem>
                            ))}
                        </div>

                        <div className="form-section">
                            <h3>Mentoring Plan</h3>
                            <p className="section-description">Define how each role will contribute to the goals' success</p>
                            {this.state.formData.slice(1, 3).map((item, index) => (
                                <FormItem {...formItemLayout} label={item.label} key={index + 1} className="form-item">
                                    {getFieldDecorator(item.fieldDecorator, {
                                        rules: [{ required: item.required, message: `${item.label} is required` }],
                                    })(
                                        <TextArea
                                            rows={4}
                                            size="large"
                                            placeholder={item.placeholder}
                                            className="textarea-field"
                                        />
                                    )}
                                </FormItem>
                            ))}
                        </div>

                        <div className="form-section">
                            <h3>Mastery Reflections</h3>
                            <p className="section-description">Add reflections as you work toward the goals (optional)</p>
                            <div className="reflections-grid">
                                <div className="reflection-group">
                                    <h4>First Reflection</h4>
                                    {this.state.formData.slice(3, 5).map((item, index) => (
                                        <FormItem {...formItemLayout} label={item.label} key={index + 3} className="form-item">
                                            {getFieldDecorator(item.fieldDecorator, {
                                                rules: [{ required: item.required, message: `${item.label} is required` }],
                                            })(
                                                item.type === 'textarea' ? (
                                                    <TextArea
                                                        rows={3}
                                                        size="large"
                                                        placeholder={item.placeholder}
                                                        className="textarea-field"
                                                    />
                                                ) : item.type === 'date' ? (
                                                    <DatePicker size="large" style={{ width: '100%' }} />
                                                ) : null
                                            )}
                                        </FormItem>
                                    ))}
                                </div>
                                <div className="reflection-group">
                                    <h4>Second Reflection</h4>
                                    {this.state.formData.slice(5, 7).map((item, index) => (
                                        <FormItem {...formItemLayout} label={item.label} key={index + 5} className="form-item">
                                            {getFieldDecorator(item.fieldDecorator, {
                                                rules: [{ required: item.required, message: `${item.label} is required` }],
                                            })(
                                                item.type === 'textarea' ? (
                                                    <TextArea
                                                        rows={3}
                                                        size="large"
                                                        placeholder={item.placeholder}
                                                        className="textarea-field"
                                                    />
                                                ) : item.type === 'date' ? (
                                                    <DatePicker size="large" style={{ width: '100%' }} />
                                                ) : null
                                            )}
                                        </FormItem>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>Our Solutions</h3>
                            <p className="section-description">Describe the solutions you are putting in place to reach the goals</p>
                            {this.state.formData.slice(7, 8).map((item, index) => (
                                <FormItem {...formItemLayout} label={item.label} key={index + 7} className="form-item">
                                    {getFieldDecorator(item.fieldDecorator, {
                                        rules: [{ required: item.required, message: `${item.label} is required` }],
                                    })(
                                        <TextArea
                                            rows={5}
                                            size="large"
                                            placeholder={item.placeholder}
                                            className="textarea-field"
                                        />
                                    )}
                                </FormItem>
                            ))}
                        </div>

                        <div className="form-actions">
                            <Button
                                onClick={this.handleCancel}
                                size="large"
                                className="cancel-button"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                disabled={this.state.submitting}
                                className="submit-button"
                            >
                                {this.state.submitting ? (
                                    <span><Icon type="loading" /> Saving Goals...</span>
                                ) : (
                                    <span><Icon type="plus" /> Save Goals</span>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal>

                <style jsx>{`
                    .ant-form-item-label {
                        text-align: left;
                    }
                    .form-container {
                        display: flex;
                        justify-content: flex-end;
                    }
                    :global(.add-goals-btn.ant-btn-primary) {
                        background: #1098f7;
                        border-color: #1098f7;
                        border-radius: 10px;
                        font-weight: 600;
                    }
                    :global(.add-goals-btn.ant-btn-primary:hover),
                    :global(.add-goals-btn.ant-btn-primary:focus) {
                        background: #0b86dd;
                        border-color: #0b86dd;
                    }
                    :global(.learning-modal .ant-modal-content) {
                        border-radius: 16px;
                        overflow: hidden;
                    }
                    :global(.learning-modal .ant-modal-header) {
                        background: #f0f7ff;
                        border-bottom: 1px solid #cfe3ff;
                        padding: 20px 24px;
                    }
                    :global(.learning-modal .ant-modal-title) {
                        font-size: 20px;
                        font-weight: 800;
                        color: #1e293b;
                    }
                    .modal-header {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }
                    .modal-logo {
                        height: 28px;
                        width: auto;
                    }
                    .learning-form {
                        padding: 20px 0;
                    }
                    .form-section {
                        margin-bottom: 32px;
                    }
                    .form-section h3 {
                        font-size: 18px;
                        font-weight: 700;
                        color: #1e293b;
                        margin-bottom: 8px;
                        padding-bottom: 8px;
                        border-bottom: 1px solid #e8eef5;
                    }
                    .section-description {
                        font-size: 14px;
                        color: #64748b;
                        margin-bottom: 20px;
                    }
                    .reflections-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 24px;
                    }
                    .reflection-group {
                        border: 1px solid #e8eef5;
                        border-radius: 16px;
                        padding: 20px;
                        background: #f8fafc;
                    }
                    .reflection-group h4 {
                        margin: 0 0 15px 0;
                        color: #1e293b;
                        font-size: 16px;
                        font-weight: 700;
                    }
                    :global(.form-item .ant-form-item-label > label) {
                        font-weight: 600;
                        color: #1e293b;
                        font-size: 14px;
                    }
                    :global(.form-item .ant-input, .form-item .ant-select-selection, .form-item .ant-calendar-picker-input) {
                        border: 1px solid #dbe6f2;
                        border-radius: 10px;
                        transition: all 0.2s ease;
                    }
                    :global(.form-item .ant-input:focus, .form-item .ant-select-selection:focus, .form-item .ant-calendar-picker-input:focus) {
                        border-color: #1098f7;
                        box-shadow: 0 0 0 2px rgba(16, 152, 247, 0.18);
                    }
                    :global(.textarea-field) {
                        border: 1px solid #dbe6f2;
                        border-radius: 10px;
                        transition: all 0.2s ease;
                    }
                    :global(.textarea-field:focus) {
                        border-color: #1098f7;
                        box-shadow: 0 0 0 2px rgba(16, 152, 247, 0.18);
                    }
                    .form-actions {
                        display: flex;
                        justify-content: flex-end;
                        gap: 15px;
                        margin-top: 30px;
                        padding-top: 20px;
                        border-top: 1px solid #e8eef5;
                    }
                    :global(.submit-button.ant-btn-primary) {
                        background: #1098f7;
                        border-color: #1098f7;
                        border-radius: 10px;
                        font-weight: 600;
                    }
                    :global(.submit-button.ant-btn-primary:hover),
                    :global(.submit-button.ant-btn-primary:focus) {
                        background: #0b86dd;
                        border-color: #0b86dd;
                    }
                    :global(.cancel-button) {
                        border-radius: 10px;
                        color: #475569;
                    }
                    @media (max-width: 768px) {
                        .reflections-grid {
                            grid-template-columns: 1fr;
                        }
                        .form-actions {
                            flex-direction: column;
                        }
                    }
                `}</style>
            </div>
        );
    }
}

export default Form.create()(ProfessionalLearningReportForm);